// Deterministic geographic matching. A wrong timezone ID can have the correct
// UTC offset, so do not substitute an offset-equivalent city or default to Vancouver.
const provinceAliases = {
  alberta: "AB", britishcolumbia: "BC", manitoba: "MB", newbrunswick: "NB",
  newfoundland: "NL", newfoundlandandlabrador: "NL", northwestterritories: "NT",
  novascotia: "NS", nunavut: "NU", ontario: "ON", princeedwardisland: "PE",
  quebec: "QC", saskatchewan: "SK", yukon: "YT", yukonterritory: "YT"
};

const normalize = value => String(value || "")
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase().replace(/[^a-z0-9]/g, "");

const provinceCode = value => {
  const key = normalize(value);
  return /^[a-z]{2}$/.test(key) ? key.toUpperCase() : provinceAliases[key];
};

const getProvince = address => {
  const iso = address["ISO3166-2-lvl4"];
  // A conflicting or foreign ISO province must never be discarded.
  if (iso && !/^CA-[A-Z]{2}$/i.test(iso)) return { invalid: true };
  const fromIso = iso ? iso.split("-")[1].toUpperCase() : undefined;
  const fromField = provinceCode(address.province || address.state);
  if (fromIso && fromField && fromIso !== fromField) return { invalid: true };
  return { code: fromIso || fromField };
};

const supportsProvince = (option, code) =>
  !code || !option.dependOnValue ||
  String(option.dependOnValue).split(/[\s,;|]+/).some(value => value.toUpperCase() === code);

/**
 * Return an exact supported IANA city ID, or the only configured option for a
 * recognized Canadian province when no timezone has already been selected.
 * Ambiguous, incomplete and conflicting locations require manual selection.
 * Returning undefined preserves an existing profile timezone in callers.
 */
export const matchOSMCityWithTimeZone = (timezoneField, locationData) => {
  const address = locationData && locationData.address;
  const options = timezoneField && timezoneField.options;
  if (!address || !Array.isArray(options) || !options.length) return;
  if (address.country_code && String(address.country_code).toLowerCase() !== "ca") return;
  if (address.country && !["canada", "ca"].includes(normalize(address.country))) return;

  const province = getProvince(address);
  if (province.invalid) return;
  const city = normalize(address.city || address.town || address.village || address.hamlet);
  if (city) {
    const candidates = options.filter(option => option && typeof option.name === "string" &&
      normalize(option.name.split("/").pop()) === city);
    const matches = candidates.filter(option => supportsProvince(option, province.code));
    if (matches.length === 1) return matches[0].name;
    // City/province conflict or duplicate city options must not turn into a
    // province-only guess (which may select a different IANA identifier).
    if (candidates.length) return;
  }

  // A recognized province alone is insufficient when several zones are offered.
  // Never overwrite a prior explicit choice with a province fallback.
  if (!province.code || timezoneField.value) return;
  const matches = options.filter(option => option && option.dependOnValue &&
    supportsProvince(option, province.code));
  if (matches.length === 1) return matches[0].name;
};
